<script setup>
import { computed, inject, ref } from 'vue';
import { styleVars } from './shared.js';
import { heroFocusPosition } from '../heroImage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// A visitor sees only cards with something on them, no "Card photo"
// frame on a card without one, and no block at all when no card has
// anything; the editor keeps every card and every prompt (Mus,
// 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const cardHasContent = (c) => Boolean(c && (c.image || c.title || c.description || c.link_label || c.badge));
const cards = computed(() => {
    const all = Array.isArray(props.config.cards) ? props.config.cards : [];
    return editing ? all : all.filter(cardHasContent);
});

// Snap scrolling does the moving; the arrows are for a mouse, which
// has nothing to swipe with. They step by one card, measured off the
// rail rather than written down, so a card of any width still lands.
const railEl = ref(null);
function step(dir) {
    const rail = railEl.value;
    if (!rail) return;
    const card = rail.querySelector('.th-slider__card');
    const by = card ? card.offsetWidth + 20 : Math.round(rail.clientWidth * 0.8);
    rail.scrollBy({ left: dir * by, behavior: 'smooth' });
}
</script>

<template>
    <section v-if="cards.length || editing" :id="config.anchor_id || null" class="th-block th-sec th-slider" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.subtitle || cards.length > 1" class="th-head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                    <p v-if="config.subtitle" class="th-lede th-slider__sub">{{ config.subtitle }}</p>
                </div>
                <div v-if="cards.length > 1" class="th-slider__nav">
                    <button type="button" class="th-slider__arrow" aria-label="Scroll left" @click="step(-1)">←</button>
                    <button type="button" class="th-slider__arrow" aria-label="Scroll right" @click="step(1)">→</button>
                </div>
            </div>

            <div v-if="cards.length" ref="railEl" class="th-slider__rail">
                <component v-for="(card, i) in cards" :key="i"
                           :is="card.link_href ? 'a' : 'div'"
                           :href="card.link_href || null"
                           class="th-card th-slider__card">
                    <div v-if="card.image || card.badge || editing" class="th-media th-slider__media">
                        <img v-if="card.image" :src="card.image" alt="" :style="{ objectPosition: heroFocusPosition(card) }">
                        <span v-else-if="editing" class="th-media__label">Card photo</span>
                        <span v-if="card.badge" class="th-slider__badge">{{ card.badge }}</span>
                    </div>
                    <h3 v-if="card.title" class="th-h3 th-slider__title">{{ card.title }}</h3>
                    <p v-if="card.description" class="th-small">{{ card.description }}</p>
                    <span v-if="card.link_label" class="th-slider__link">{{ card.link_label }} →</span>
                </component>
            </div>
            <p v-else-if="editing" class="th-small th-slider__empty">Add cards in the editor to fill this row.</p>
        </div>
    </section>
</template>

<style scoped>
.th-slider__sub { margin-top: 12px; }
.th-slider__nav { display: flex; gap: 8px; }
.th-slider__arrow {
    width: 40px;
    height: 40px;
    border: 1px solid var(--th-line);
    background: #fff;
    border-radius: var(--r);
    color: var(--th-ink);
    font-size: 16px;
    line-height: 1;
}
.th-slider__arrow:hover { background: var(--tint); }

.th-slider__rail {
    display: flex;
    gap: 20px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    /* Room for the cards' own shadowless border on the first and last
       card, and for the gutter the wrap already pays. */
    padding-bottom: 4px;
    scrollbar-width: thin;
}
.th-slider__card {
    flex: 0 0 min(100%, 300px);
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-decoration: none !important;
}
a.th-slider__card:hover { border-color: var(--accent); }
.th-slider__media { aspect-ratio: 16 / 10; flex: none; }
.th-slider__badge {
    position: absolute;
    top: 10px;
    left: 10px;
    background: var(--accent);
    color: #fff;
    padding: 5px 8px;
    border-radius: 3px;
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .08em;
    text-transform: uppercase;
}
.th-slider__title { font-size: 20px; }
.th-slider__link { margin-top: auto; font-weight: 600; font-size: 15px; border-bottom: 2px solid var(--accent); align-self: flex-start; }
.th-slider__empty { font-style: italic; }
</style>
